import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import PDA1131_R01Store from "./PDA1131_R01Store";
import { usePDA1131_R01Content } from "./PDA1131_R01Content";

/** 출하지시 상세 — itemCd 스캔, 출고수량 입력, 검수/저장/취소 */
const PDA1131_R01DetailInfo = () => {
  // 출하지시 상세·스캔 입력·스캔 결과·검수 대기 목록
  const {
    shippingDetail,
    itemCdInput,
    setItemCdInput,
    scannedItem,
    inspectedItems,
  } = PDA1131_R01Store();

  // 스캔·검수·일괄저장·취소 비즈니스 로직 (Content 훅)
  const { handleScanItem, handleInspect, handleSaveAll, handleCancel } =
    usePDA1131_R01Content();

  /** 품목 바코드/코드 입력란 ref (스캔 후 포커스 복귀용) */
  const itemInputRef = useRef<HTMLInputElement>(null);
  /** 출고수량 입력란 ref (스캔 성공 시 포커스 이동용) */
  const shipQtyInputRef = useRef<HTMLInputElement>(null);
  /** 출고수량 입력값 (로컬 state, scannedItem과 연동) */
  const [shipQty, setShipQty] = useState<string>("");
  /** PDA 소프트키보드 표시 여부 (false면 inputMode=none) */
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  /** 더블탭 감지용 마지막 터치 시각(ms) */
  const [lastTapTime, setLastTapTime] = useState(0);
  /** 검수 대기 취소 확인 팝업 표시 여부 */
  const [confirmVisible, setConfirmVisible] = useState(false);

  /** 상세 진입 시 품목 입력란에 포커스 */
  useEffect(() => {
    if (shippingDetail && itemInputRef.current) {
      itemInputRef.current.focus();
    }
  }, [shippingDetail]);

  /** 스캔 성공: canQty 반영 + 출고수량 포커스 / 실패·경고: 출고수량 초기화 */
  useEffect(() => {
    if (scannedItem) {
      setShipQty(String(scannedItem.canQty));
      setTimeout(() => {
        shipQtyInputRef.current?.focus();
        shipQtyInputRef.current?.select();
      }, 100);
    } else {
      setShipQty("");
    }
  }, [scannedItem]);

  /** 품목 입력란 더블탭 시 소프트키보드 활성화 */
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapTime < 300) {
      setKeyboardEnabled(true);
      itemInputRef.current?.focus();
    }
    setLastTapTime(now);
  };

  /** 키보드 표시/숨김 토글 버튼 */
  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => itemInputRef.current?.focus(), 100);
    }
  };

  /** 품목 입력란 Enter → itemCd 스캔 API 호출 */
  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && itemCdInput.trim()) {
      handleScanItem();
    }
  };

  /** 검수 버튼 — 수량 검증 후 검수 대기 목록에 누적 */
  const handleInspectClick = () => {
    if (!shippingDetail || !scannedItem) {
      alert("품목을 먼저 스캔하세요.");
      return;
    }
    const qty = parseFloat(shipQty);
    if (isNaN(qty) || qty <= 0) {
      alert("출고수량을 입력하세요.");
      return;
    }
    handleInspect(qty);
    setShipQty("");
    (document.activeElement as HTMLElement)?.blur();
    setTimeout(() => itemInputRef.current?.focus(), 100);
  };

  /** 저장 버튼 — 검수 대기 품목 일괄 출고 API 호출 */
  const handleSaveAllClick = async () => {
    await handleSaveAll();
    setTimeout(() => itemInputRef.current?.focus(), 300);
  };

  /** 취소 버튼 — 검수 대기 있으면 확인 팝업, 없으면 입력만 초기화 */
  const handleCancelClick = () => {
    if (inspectedItems.length > 0) {
      setConfirmVisible(true);
    } else {
      handleCancel();
      setTimeout(() => itemInputRef.current?.focus(), 100);
    }
  };

  /** 취소 확인 팝업 — 검수 대기 전체 삭제 후 입력 초기화 */
  const handleConfirmOk = () => {
    setConfirmVisible(false);
    handleCancel();
    setTimeout(() => itemInputRef.current?.focus(), 100);
  };

  /** 취소 확인 팝업 — 닫기만 (검수 대기 유지) */
  const handleConfirmClose = () => {
    setConfirmVisible(false);
    setTimeout(() => itemInputRef.current?.focus(), 100);
  };

  return (
    <>
      {/* 취소 확인 팝업 */}
      {confirmVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleConfirmClose}
          />
          <div className="relative bg-white rounded-xl shadow-2xl w-[300px] overflow-hidden">
            <div className="flex flex-col items-center pt-6 pb-4 px-6">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-3">
                <svg
                  className="w-8 h-8 text-orange-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
              </div>
              <p className="text-center text-gray-800 text-base font-medium whitespace-pre-line leading-relaxed">
                {`검수 대기중인 ${inspectedItems.length}건의 항목이 있습니다.\n모두 취소하시겠습니까?`}
              </p>
            </div>
            <div className="flex border-t border-gray-200">
              <button
                onClick={handleConfirmClose}
                className="flex-1 py-3 text-base text-gray-600 font-medium hover:bg-gray-50 transition-colors border-r border-gray-200"
              >
                아니오
              </button>
              <button
                onClick={handleConfirmOk}
                className="flex-1 py-3 text-base text-orange-500 font-bold hover:bg-orange-50 transition-colors"
              >
                취소하기
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 bg-white border-b border-gray-200">
        {shippingDetail ? (
          <div className="space-y-1">
            {/* 지시번호 */}
            <div className="flex items-center gap-2">
              <label className="w-24 text-lg font-medium text-black">지시번호</label>
              <input
                type="text"
                value={shippingDetail.orderNo || ""}
                disabled
                className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
              />
            </div>

            {/* 품목 스캔 */}
            <div className="flex items-center gap-2">
              <label className="w-24 text-lg font-medium text-black">품목</label>
              <div className="flex-1 flex gap-1">
                <input
                  ref={itemInputRef}
                  type="text"
                  value={itemCdInput}
                  onChange={(e) => setItemCdInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onClick={handleDoubleTap}
                  inputMode={keyboardEnabled ? "text" : "none"}
                  placeholder="품목 스캔 후 Enter"
                  autoComplete="off"
                  className="flex-1 px-2 max-w-[195px] text-lg border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={toggleKeyboard}
                  className={`w-10 flex-shrink-0 px-2 py-0.5 border border-gray-300 rounded transition-colors ${
                    keyboardEnabled
                      ? "bg-blue-500 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                  title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </button>
              </div>
            </div>

            {/* 품명 */}
            <div className="flex items-center gap-2">
              <label className="w-24 text-lg font-medium text-black">품명</label>
              <input
                type="text"
                value={scannedItem?.itemNm || ""}
                disabled
                className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
              />
            </div>

            {/* 규격 */}
            <div className="flex items-center gap-2">
              <label className="w-24 text-lg font-medium text-black">규격</label>
              <input
                type="text"
                value={scannedItem?.itemSpec || ""}
                disabled
                className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
              />
            </div>

            {/* 가용재고 / 지시잔량 */}
            <div className="flex items-center gap-2">
              <label className="w-24 text-lg font-medium text-black">재고</label>
              <input
                type="text"
                value={
                  scannedItem
                    ? `${scannedItem.availableQty.toLocaleString()} ${scannedItem.itemUnit}`
                    : ""
                }
                disabled
                className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100"
              />
            </div>

            {/* 출고수량 (기본값 = canQty) */}
            <div className="flex items-center gap-2">
              <label className="w-24 text-lg font-medium text-black">출고수량</label>
              <input
                ref={shipQtyInputRef}
                type="number"
                value={shipQty}
                onChange={(e) => setShipQty(e.target.value)}
                disabled={!scannedItem}
                placeholder="출고수량 입력"
                className="flex-1 px-2 text-lg border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleInspectClick}
                disabled={!scannedItem || !shipQty}
                className="flex-1 py-2 text-lg bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                검수
              </button>
              <button
                onClick={handleSaveAllClick}
                disabled={inspectedItems.length === 0}
                className="flex-1 py-2 text-lg bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                저장
              </button>
              <button
                onClick={handleCancelClick}
                className="flex-1 py-2 text-lg bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                취소
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            출하지시를 선택하세요
          </div>
        )}
      </div>
    </>
  );
};

export default PDA1131_R01DetailInfo;
